/**
 * テナント管理ページ コンテナ
 */
import type React from "react";
import { useCallback, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../../../hooks/authProvider/authProvider";
import {
  useTenantMatchingSettings,
  useUpdateTenantMatchingSettings,
} from "../../../hooks/use-matching-conditions-api";
import {
  useDisconnectSlack,
  useSlackChannels,
  useSlackIntegration,
  useSlackOAuthCallback,
  useTestSlackNotification,
  useUpdateSlackChannel,
} from "../../../hooks/use-slack-integration-api";
import {
  type UpdateTenantInput,
  useChangeMemberRole,
  useTenantDetailApi,
  useUpdateTenantApi,
} from "../../../hooks/useTenantApi";
import { getCurrentTenant } from "../../../utils/authority-utils";
import { TenantPage } from "./TenantPage";

/** メール取り込み環境 */
const INTAKE_ENV = process.env.REACT_APP_ENV === "prd" ? "prd" : "dev";

const TenantPageContainer: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const tenant = getCurrentTenant(currentUser ?? null);
  const tenantId = tenant?.id ?? "";

  const { data, loading, error, refetch } = useTenantDetailApi(tenantId);
  const { updateTenant, loading: isUpdating } = useUpdateTenantApi();
  const { changeMemberRole, loading: isRoleChanging } = useChangeMemberRole();

  const currentUserId = currentUser?.id ?? "";

  const {
    settings: tenantMatchingConditions,
    loading: tenantSettingsLoading,
    refetch: refetchTenantSettings,
  } = useTenantMatchingSettings();
  const { updateSettings: updateTenantSettings, loading: isTenantUpdating } =
    useUpdateTenantMatchingSettings();

  // Slack連携
  const {
    integration: slackIntegration,
    loading: slackLoading,
    refetch: refetchSlack,
  } = useSlackIntegration();
  const { disconnect: disconnectSlack, loading: isDisconnecting } =
    useDisconnectSlack();
  const { channels: slackChannels, fetchChannels } = useSlackChannels();
  const { updateChannel, loading: isChannelUpdating } = useUpdateSlackChannel();
  const { sendTest, loading: isTestSending } = useTestSlackNotification();
  const { processCallback } = useSlackOAuthCallback();

  // Slack OAuth コールバック中継処理
  // SlackOAuthCallbackPage が sessionStorage に退避した code を処理する
  const slackCallbackProcessed = useRef(false);
  useEffect(() => {
    if (slackCallbackProcessed.current) return;
    const code = sessionStorage.getItem("slack_oauth_code");
    if (!code) return;
    slackCallbackProcessed.current = true;
    sessionStorage.removeItem("slack_oauth_code");

    processCallback(code)
      .then(() => refetchSlack())
      .catch(e => {
        console.error("Slack OAuth callback failed:", e);
      });
  }, [processCallback, refetchSlack]);

  const intakeAddress = `${tenantId}@intake-${INTAKE_ENV}.strixsearch.com`;

  const handleUpdate = useCallback(
    async (input: UpdateTenantInput) => {
      await updateTenant(tenantId, input);
      await refetch();
    },
    [tenantId, updateTenant, refetch],
  );

  const handleRoleChange = useCallback(
    async (userId: string, newRole: string) => {
      await changeMemberRole(tenantId, userId, newRole);
      await refetch();
    },
    [tenantId, changeMemberRole, refetch],
  );

  const handleAutoMatchingToggle = useCallback(
    async (enabled: boolean) => {
      await updateTenantSettings(enabled);
      await refetchTenantSettings();
    },
    [updateTenantSettings, refetchTenantSettings],
  );

  const handleSlackConnect = useCallback(() => {
    const clientId = process.env.REACT_APP_SLACK_CLIENT_ID;
    if (!clientId) return;

    const state = crypto.randomUUID();
    sessionStorage.setItem("slack_oauth_state", state);

    const redirectUri = `${window.location.origin}/settings/slack/callback`;
    const scopes = "chat:write,channels:read,groups:read";
    const url = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&scope=${scopes}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
    window.location.href = url;
  }, []);

  const handleSlackDisconnect = useCallback(async () => {
    await disconnectSlack();
    await refetchSlack();
  }, [disconnectSlack, refetchSlack]);

  const handleSlackChannelChange = useCallback(
    async (channelId: string, channelName: string) => {
      await updateChannel(channelId, channelName);
      await refetchSlack();
    },
    [updateChannel, refetchSlack],
  );

  const handleSlackTestSend = useCallback(async () => {
    return await sendTest();
  }, [sendTest]);

  if (loading || tenantSettingsLoading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error.message}</span>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <TenantPage
      tenant={data}
      intakeAddress={intakeAddress}
      onUpdate={handleUpdate}
      isUpdating={isUpdating}
      currentUserId={currentUserId}
      onRoleChange={handleRoleChange}
      isRoleChanging={isRoleChanging}
      autoMatchingEnabled={
        tenantMatchingConditions?.autoMatchingEnabled ?? false
      }
      onAutoMatchingToggle={handleAutoMatchingToggle}
      isAutoMatchingUpdating={isTenantUpdating}
      slackIntegration={slackIntegration}
      slackLoading={slackLoading}
      slackChannels={slackChannels}
      onSlackConnect={handleSlackConnect}
      onSlackDisconnect={handleSlackDisconnect}
      onSlackChannelChange={handleSlackChannelChange}
      onSlackTestSend={handleSlackTestSend}
      onSlackChannelsFetch={fetchChannels}
      isSlackUpdating={isDisconnecting || isChannelUpdating || isTestSending}
    />
  );
};

export default TenantPageContainer;
